// @owners { team: patient-fulfillment }
// This is now a legacy component that will be removed in favor of ItemPaymentBreakdown.tsx
// when the Improved Pricing & Savings Attribution experiment is fully implemented.
import { type PrescriptionPricing } from '@alto/scriptdash/alto/pricing/patients/v3/pricing_endpoint';
import React from 'react';
import getSelectedPaymentTypeForPrescription from '~shared/features/checkout/selectors/getSelectedPaymentTypeForPrescription';
import {
  NEW_ESTIMATED_PREFIX_COPY,
  PRESCRIPTION_PRICING_LABELS,
  PRICE_PENDING,
} from '~shared/features/pricing/constants';
import { getItemPricingTotal } from '~shared/features/pricing/selectors/getPricingInfoForPrescription';
import { type Pricing } from '~shared/features/pricing/types';
import { formatDollarsWithPlaceholder } from '~shared/helpers/currency';
import { useSelectorShared } from '~shared/store';
import { PricingBreakdownRxLineItemWithoutInsurance } from './PricingBreakdownRxLineItemWithoutInsurance';
import { PricingBreakdownRxLineItemsWithInsurance } from './PricingBreakdownRxLineItemsWithInsurance';

type Props = {
  readonly pricingItem: Pricing | PrescriptionPricing;
};

const PRICING_BREAKDOWN_HELPER_TEXT = {
  UNAVAILABLE: 'We are still calculating, and will confirm the final price with you as soon as it’s available.',
  WITHOUT_INSURANCE: 'Paying without insurance',
};

export const PricingBreakdownRxLineItems = ({ pricingItem }: Props) => {
  const selectedPaymentType = useSelectorShared((state) =>
    getSelectedPaymentTypeForPrescription(state, { prescriptionID: pricingItem.prescription_id }),
  );
  const { reason, value, type } = getItemPricingTotal(pricingItem, selectedPaymentType);

  if (reason === PRESCRIPTION_PRICING_LABELS.PRICE_UNAVAILABLE) {
    // when the price is 'unavailable' show helper text and pending
    return (
      <PricingBreakdownRxLineItemWithoutInsurance
        label={PRESCRIPTION_PRICING_LABELS.YOUR_COPAY}
        price={PRICE_PENDING}
        helperText={PRICING_BREAKDOWN_HELPER_TEXT.UNAVAILABLE}
      />
    );
  }

  if (reason === PRESCRIPTION_PRICING_LABELS.YOUR_COPAY && type === 'without_insurance') {
    // when cheapest and best option for patient is cash
    return (
      <PricingBreakdownRxLineItemWithoutInsurance
        label={PRESCRIPTION_PRICING_LABELS.YOUR_COPAY}
        price={formatDollarsWithPlaceholder(value)}
        helperText={PRICING_BREAKDOWN_HELPER_TEXT.WITHOUT_INSURANCE}
      />
    );
  }

  if (reason === PRESCRIPTION_PRICING_LABELS.LAST_COPAY) {
    return (
      <PricingBreakdownRxLineItemWithoutInsurance
        label={PRESCRIPTION_PRICING_LABELS.LAST_COPAY}
        price={`${NEW_ESTIMATED_PREFIX_COPY}${formatDollarsWithPlaceholder(value)}`}
        helperText={PRICING_BREAKDOWN_HELPER_TEXT.UNAVAILABLE}
      />
    );
  }

  if (reason === PRESCRIPTION_PRICING_LABELS.ESTIMATED_PRICE) {
    // when the price is only estimated
    return (
      <PricingBreakdownRxLineItemWithoutInsurance
        label={PRESCRIPTION_PRICING_LABELS.YOUR_COPAY}
        price={`${NEW_ESTIMATED_PREFIX_COPY}${formatDollarsWithPlaceholder(value)}`}
        helperText={PRICING_BREAKDOWN_HELPER_TEXT.UNAVAILABLE}
      />
    );
  }

  // insurance, coupons, credits, etc. case
  return <PricingBreakdownRxLineItemsWithInsurance pricingItem={pricingItem} />;
};
